import type { QuoteInterval } from "@/v2Projects/types";

export const quoteCopy = {
  pricingModelLabel: "Pricing model",
  intervalOptions: [
    { value: "one_time", label: "Fixed Price" },
    { value: "hourly", label: "Hourly" },
    { value: "hours_bank", label: "Prepaid Hours" },
  ] satisfies { value: QuoteInterval; label: string }[],
  amountLabels: {
    one_time: {
      single: "Fixed fee amount",
      multiple: "Unit price",
      helperSingle: "The total amount for the full fixed-fee scope.",
      helperMultiple: "Price for each deliverable or unit.",
    },
    hourly: {
      label: "Hourly rate",
      helper: "Rate per hour before estimated hours are applied.",
    },
    hours_bank: {
      label: "Hourly rate",
      helper: "Rate per hour for the prepaid hours bank.",
    },
  },
  quantityLabels: {
    one_time: {
      label: "Number of deliverables",
      helper: "Used to calculate the total from your unit price.",
      stripLabel: "Deliverables",
      unit: "deliverable",
    },
    hourly: {
      label: "Estimated hours",
      helper: "Used to calculate the estimated total.",
      stripLabel: "Est. time",
      unit: "hour",
    },
    hours_bank: {
      label: "Prepaid hours",
      helper: "Used to calculate the prepaid hours total.",
      stripLabel: "Prepaid hours",
      unit: "hour",
    },
  },
  rateLabels: {
    one_time: {
      single: "Fixed price",
      multiple: "Unit price",
    },
    hourly: "Hourly rate",
    hours_bank: "Hourly rate",
  },
  totalLabels: {
    one_time: "Total",
    hourly: "Total",
    hours_bank: "Prepaid hours total",
  },
  budgetSuffixes: {
    hours_bank: "bank hours",
  },
  hoursBankExpirationDisplayLabel: "Hours expire after",
  hoursBankExpirationTermsDisplayLabel: "Expiration terms",
  fixedFeeQuantityCheckbox: "Price by quantity",
  fixedFeeQuantityHelper:
    "The total depends on the number of deliverables, or items included.",
  hoursBankExpirationCheckbox: "Set an expiration date for the hours",
  hoursBankExpirationDescription:
    "Hours unused after this date will expire. Leave off for no expiration.",
  hoursBankExpirationDaysLabel: "Expiration window",
  hoursBankExpirationDaysHelper:
    "Number of days after purchase before unused hours expire.",
  hoursBankExpirationDaysUnit: "days",
  hoursBankExpirationTermsLabel: "Expiration terms",
  hoursBankExpirationTermsHelper:
    "Describe when unused hours expire and any terms the client should know.",
  hoursBankExpirationTermsPlaceholder:
    "Describe when unused hours expire and any terms the client should know.",
  deliveryTimelineLabel: "Delivery timeline",
  deliveryTimelineHelper:
    "How soon the client can expect the work, counted as calendar days or business days (which exclude weekends).",
  deliveryTimelineDaysPlaceholder: "14",
  deliveryTimelineDayUnits: [
    { value: "calendar_days", label: "Calendar Days" },
    { value: "business_days", label: "Business Days" },
  ],
  externalPaymentLinkLabel: "External payment link",
  externalPaymentLinkHelper:
    "Clients will see a Pay Now action that first confirms the link opens outside Shopexperts.",
  scopeLabel: "Scope of work",
  scopeHelper: "Outline deliverables, milestones, and what the client should expect.",
  feeEstimateHeading: "Breakdown",
  feeEstimateNetLabel: "Net earnings",
  feeEstimateLoading: "Calculating fee estimate…",
  feeEstimateDisclaimer:
    "This is a close estimate. Final processing fees may vary based on the payment provider.",
  validation: {
    amount: "Please enter a valid amount",
    quantity: "Please enter a valid quantity",
    deliveryDays: "Delivery days must be 0 or more",
    paymentLink: "Please enter a valid payment link",
    hoursBankExpirationDays: "Expiration days must be 0 or more",
    hoursBankExpirationTerms: "Please enter expiration terms",
    created: "Quote created",
    failed: "Failed to create quote",
  },
  proposalDetails: {
    viewDetailsAction: "View Proposal Details",
    title: "Review Proposal Details",
    closeLabel: "Close proposal details",
    expertOfferingLabel: "Expert offering",
    expertRoleFallback: "Shopify expert",
    submittedLabel: "Submitted",
    estimatedDeliveryLabel: "Estimated delivery",
    hourlyRateDetail: "per hour",
    validForPrefix: "valid for",
    scopeTitle: "Scope of Proposal",
    noScopeFallback: "No scope was provided for this proposal.",
    timelineTitle: "Proposal timeline",
    timelineSubmitted: "Proposal submitted",
    timelineViewed: "Review proposal",
    timelinePaymentCompleted: "Payment completed",
    timelineKickoff: "Project kick-off",
    upcomingLabel: "Upcoming",
    pendingLabel: "Pending",
    kickoffReadyLabel: "Work can begin now",
    notAvailable: "Not available",
    invoiceUrlLabel: "Invoice URL",
    invoiceMissingFallback: "Invoice URL is not available yet.",
    copyAction: "Copy",
    copiedToast: "Copied",
    rejectAction: "Reject",
    acceptAction: "Accept",
    payNowAction: "Pay Now",
    openInvoiceAction: "Open invoice",
    cancelAction: "Cancel",
    rejectedToast: "Quote rejected",
    externalPaymentDialogTitle: "Open external payment link?",
    externalPaymentDialogText:
      "This payment link is outside Shopexperts and was provided directly by the expert. Continue only if you trust the destination.",
    externalPaymentContinueAction: "Continue to Payment",
    cardHeadlineReceived: (senderName: string) => `You received a proposal from ${senderName}.`,
    cardHeadlineSent: (senderName: string) => `You sent a proposal to ${senderName}.`,
    cardHeadlineShared: (senderName: string) => `${senderName} shared a proposal.`,
  },
} as const;

export function quoteIntervalLabel(interval: QuoteInterval | string): string {
  return (
    quoteCopy.intervalOptions.find((option) => option.value === interval)?.label ??
    interval.replace(/_/g, " ")
  );
}
